import { useState, useEffect, useCallback, useMemo } from 'react';

const useFilterKey = (storageKey = 'filterKeyList') => {
    
    const [filterKeyList, setFilterKeyList] = useState([]);
    const [loading, setLoading] = useState(true);

    
    const getStorageItem = useCallback(() => {
        try {
            let data = localStorage.getItem(storageKey);

            data = data ? JSON.parse(data) : []
            data = data?.length >= 20 ? data.slice(0, 19) : data
            
            return data;
        } catch (error) {
            console.error('Failed to parse filterKeyList from localStorage', error);
            return [];
        }
    }, [storageKey]);

    
    const processItems = useCallback((items) => {
        
        const valueMap = new Map();

        items.forEach(item => {
            if (!valueMap.has(item.value)) {
                valueMap.set(item.value, item);
            } else if (item.ts > valueMap.get(item.value).ts) {
                valueMap.set(item.value, item);
            }
        });

        return Array.from(valueMap.values())
            .sort((a, b) => b.ts - a.ts);
    }, []);

    
    const init = useCallback((items) => {
        if (!Array.isArray(items)) {
            console.error('init function requires an array as input');
            return;
        }

        const normalizedItems = items.map(item => {
            if (typeof item === 'string') {
                return { value: item, ts: Date.now() };
            }
            return {
                value: item.value || '',
                ts: item.ts || Date.now()
            };
        }).concat(getStorageItem());

        const processedItems = processItems(normalizedItems);

        localStorage.setItem(storageKey, JSON.stringify(processedItems));

        setFilterKeyList(processedItems);
    }, [storageKey, processItems]);

    
    const setStorageItem = useCallback((value) => {
        if (!value) return;

        setFilterKeyList(prevList => {
            
            const newItem = { value, ts: Date.now() };

            const newList = processItems([...prevList, newItem]);

            localStorage.setItem(storageKey, JSON.stringify(newList));

            return newList;
        });
    }, [storageKey, processItems]);


    const setMultipleItems = useCallback((values) => {
        if (!Array.isArray(values)) return;

        setFilterKeyList(prevList => {

            const newItems = values.map(value => ({ value, ts: Date.now() }));

            
            const newList = processItems([...prevList, ...newItems]);

            localStorage.setItem(storageKey, JSON.stringify(newList));

            return newList;
        });
    }, [storageKey, processItems]);

    
    const clearStorage = useCallback(() => {
        localStorage.removeItem(storageKey);
        setFilterKeyList([]);
    }, [storageKey]);

    
    const removeItem = useCallback((value) => {
        setFilterKeyList(prevList => {
            const newList = prevList.filter(item => item.value !== value);

            localStorage.setItem(storageKey, JSON.stringify(newList));

            return newList;
        });
    }, [storageKey]);


    const getLatestItem = useCallback(() => {
        if (filterKeyList.length === 0) return null;
        return filterKeyList[0];
    }, [filterKeyList]);

    
    const getOldestItem = useCallback(() => {
        if (filterKeyList.length === 0) return null;
        return filterKeyList[filterKeyList.length - 1];
    }, [filterKeyList]);

    
    const hasItem = useCallback((value) => {
        return filterKeyList.some(item => item.value === value);
    }, [filterKeyList]);

    
    useEffect(() => {
        const data = getStorageItem();
        
        const processedData = processItems(data);
        setFilterKeyList(processedData);
        setLoading(false);
    }, [getStorageItem, processItems]);


    const memoizedFilterKeyList = useMemo(() => filterKeyList, [filterKeyList]);

    return {
        filterKeyList: memoizedFilterKeyList,  // curr filterKeyList
        loading,                              // is loading
        setStorageItem,                       // add new item (rm repeat item and sort by timestamp）
        getStorageItem,                       // get items
        clearStorage,                         // clear all
        removeItem,                           // delete the item
        init,                                 // init
        setMultipleItems,                     // add multi items
        getLatestItem,                        // get lastest item
        getOldestItem,                        // get oldest item
        hasItem,                              // has this item
        count: filterKeyList.length           // item length
    };
};

export default useFilterKey;